import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Search, MessageSquare, Phone, User, Clock, CheckCircle, AlertCircle, Download, Filter, Calendar } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Conversations() {
  const { getAuthHeader } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedConversation, setSelectedConversation] = useState(null);

  useEffect(() => {
    fetchConversations();
    // Poll for new conversations every 30 seconds
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/conversations`, {
        headers: getAuthHeader()
      });
      setConversations(response.data);
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateConversationStatus = async (conversationId, status) => {
    try {
      await axios.put(`${API_URL}/api/conversations/${conversationId}/status?status=${status}`, null, {
        headers: getAuthHeader()
      });
      toast.success(`Conversation marked as ${status}`);
      fetchConversations();
    } catch (error) {
      toast.error("Failed to update conversation");
    }
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = 
      conv.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.customer_phone?.includes(searchQuery) ||
      conv.customer_email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || conv.status === statusFilter;
    const matchesPlatform = platformFilter === "all" || conv.platform === platformFilter;
    
    // Date filter
    let matchesDate = true;
    if (dateFilter !== "all") {
      const convDate = new Date(conv.created_at);
      const now = new Date();
      const diffDays = Math.floor((now - convDate) / (1000 * 60 * 60 * 24));
      
      if (dateFilter === "today") matchesDate = diffDays === 0;
      else if (dateFilter === "week") matchesDate = diffDays <= 7;
      else if (dateFilter === "month") matchesDate = diffDays <= 30;
    }
    
    return matchesSearch && matchesStatus && matchesPlatform && matchesDate;
  });
  
  const exportToPDF = () => {
    // Create a simple text export for now (can be enhanced with jspdf library)
    const exportData = filteredConversations.map(conv => {
      const messages = conv.messages?.map(m => `[${m.role}] ${m.content}`).join('\n  ') || 'No messages';
      return `
Customer: ${conv.customer_name}
Platform: ${conv.platform || 'N/A'}
Contact: ${conv.customer_phone || conv.customer_email || 'N/A'}
Status: ${conv.status}
Created: ${new Date(conv.created_at).toLocaleString()}
Messages:
  ${messages}
${'='.repeat(80)}
`;
    }).join('\n');
    
    const blob = new Blob([exportData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversations-export-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Conversations exported successfully');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active": return <AlertCircle className="h-4 w-4 text-amber-500" />;
      case "resolved": return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      default: return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="conversations-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Conversations</h1>
        <p className="text-muted-foreground mt-1">Chat history and customer interactions from web and WhatsApp</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-220px)]">
        {/* Conversation List */}
        <Card className="border-border rounded-none lg:col-span-1">
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-none"
                  data-testid="conversation-search-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="rounded-none text-xs" data-testid="conversation-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={platformFilter} onValueChange={setPlatformFilter}>
                  <SelectTrigger className="rounded-none text-xs">
                    <SelectValue placeholder="Platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Platforms</SelectItem>
                    <SelectItem value="web">Web</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="rounded-none text-xs">
                  <Calendar className="h-3 w-3 mr-2" />
                  <SelectValue placeholder="Date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                onClick={exportToPDF} 
                variant="outline" 
                size="sm" 
                className="w-full rounded-none"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-380px)]">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-pulse text-muted-foreground">Loading...</div>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                  <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm">No conversations found</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredConversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => setSelectedConversation(conv)}
                      className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-muted/50' : ''
                      }`}
                      data-testid={`conversation-item-${conv.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 bg-gradient-to-r from-[#EC1A23] to-[#561D1A] flex items-center justify-center flex-shrink-0">
                            <User className="h-5 w-5 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm truncate">{conv.customer_name}</p>
                              {conv.platform === "web" && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-700 rounded font-medium">WEB</span>
                              )}
                              {conv.platform === "whatsapp" && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 rounded font-medium">WhatsApp</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground font-mono">
                              {conv.customer_phone || conv.customer_email || "No contact"}
                            </p>
                          </div>
                        </div>
                        {getStatusIcon(conv.status)}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-1">
                        {conv.messages?.[conv.messages.length - 1]?.content || "No messages"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatTimestamp(conv.updated_at)}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Conversation Detail */}
        <Card className="border-border rounded-none lg:col-span-2">
          {selectedConversation ? (
            <>
              <CardHeader className="border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-[#EC1A23] to-[#561D1A] flex items-center justify-center">
                      <User className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-['Manrope']">{selectedConversation.customer_name}</CardTitle>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="h-3 w-3" />
                        <span className="font-mono">{selectedConversation.customer_phone}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedConversation.status !== "resolved" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateConversationStatus(selectedConversation.id, "resolved")}
                        className="rounded-none"
                        data-testid="mark-resolved-btn"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Mark Resolved
                      </Button>
                    )}
                    {selectedConversation.status === "resolved" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateConversationStatus(selectedConversation.id, "active")}
                        className="rounded-none"
                        data-testid="reopen-btn"
                      >
                        Reopen
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[calc(100vh-380px)] p-4">
                  <div className="space-y-4">
                    {selectedConversation.messages?.map((msg, index) => (
                      <div
                        key={msg.id || index}
                        className={`flex ${msg.role === "user" ? "justify-start" : "justify-end"}`}
                      >
                        <div
                          className={`max-w-[80%] p-3 ${
                            msg.role === "user"
                              ? "bg-muted"
                              : "bg-gradient-to-r from-[#EC1A23] to-[#561D1A] text-white"
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            msg.role === "user" ? "text-muted-foreground" : "text-white/70"
                          }`}>
                            {formatTimestamp(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {(!selectedConversation.messages || selectedConversation.messages.length === 0) && (
                      <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                        <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
                        <p className="text-sm">No messages in this conversation</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <MessageSquare className="h-16 w-16 mb-4 opacity-30" />
              <p className="text-lg font-medium">Select a conversation</p>
              <p className="text-sm">Choose a conversation from the list to view details</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
